import { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';

interface PipelineTemperature {
  pipelineTotal: number;
  monthlyQuota: number;
  coverage: number;
}

interface ConversionRates {
  leadToOpportunity: number;
  opportunityToWon: number;
  leadsConsidered: number;
  opportunitiesClosed: number;
}

interface DashboardData {
  staleCount?: number;
}

interface Alert {
  id: string;
  severity: 'high' | 'medium';
  message: string;
  to?: string;
}

/**
 * Story 56.7 — alerts feed. Composes signals already exposed by other
 * endpoints into a small, actionable list. New alert types should land
 * here rather than scattered across the dashboard.
 */
export function AlertsFeedWidget() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient
        .get<PipelineTemperature>('/analytics/pipeline-temperature')
        .then((r) => r.data)
        .catch(() => null),
      apiClient
        .get<ConversionRates>('/analytics/conversion')
        .then((r) => r.data)
        .catch(() => null),
      apiClient
        .get<DashboardData>('/analytics/dashboards/mine')
        .then((r) => r.data)
        .catch(() => null),
    ])
      .then(([temp, conv, dash]) => {
        const next: Alert[] = [];
        // Pipeline coverage too low.
        if (temp && temp.monthlyQuota > 0 && Number(temp.coverage) < 1) {
          next.push({
            id: 'coverage-low',
            severity: 'high',
            message: `Pipeline coverage at ${(Number(temp.coverage) * 100).toFixed(0)}% of monthly quota — below 100%.`,
          });
        }
        // Stale deals.
        if (dash?.staleCount && dash.staleCount > 0) {
          next.push({
            id: 'stale-deals',
            severity: dash.staleCount > 5 ? 'high' : 'medium',
            message: `${dash.staleCount} deal${dash.staleCount === 1 ? '' : 's'} stagnant — no movement in over 14 days.`,
            to: '/opportunities?stale=true',
          });
        }
        // Lead-to-opp conversion below 20%.
        if (conv && conv.leadsConsidered >= 20 && Number(conv.leadToOpportunity) < 0.2) {
          next.push({
            id: 'low-l2o',
            severity: 'medium',
            message: `Only ${(Number(conv.leadToOpportunity) * 100).toFixed(0)}% of leads convert to opportunities — investigate qualification or sources.`,
            to: '/leads',
          });
        }
        setAlerts(next);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        Loading alerts…
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Alerts
        </h3>
      </div>
      {alerts.length === 0 ? (
        <p className="text-sm text-muted-foreground">All clear — no alerts firing right now.</p>
      ) : (
        <ul className="space-y-2">
          {alerts.map((a) => (
            <li
              key={a.id}
              className={`flex items-start gap-2 rounded-md border-l-4 px-3 py-2 text-sm ${
                a.severity === 'high'
                  ? 'border-rose-500 bg-rose-950/40/40'
                  : 'border-amber-500 bg-amber-950/40/40'
              }`}
            >
              <span className="flex-1">{a.message}</span>
              {a.to && (
                <Link to={a.to} className="text-xs text-primary hover:underline">
                  <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
